/**
 * Transform trip data to Sankey diagram format for ECharts
 */

/**
 * Get all unique zones from trip data
 */
export const getUniqueZones = (trips) => {
  const zones = new Set();
  trips.forEach((trip) => {
    zones.add(trip.origin);
    zones.add(trip.destination);
  });
  return Array.from(zones).sort();
};

/**
 * Filter trips by minimum flow threshold
 */
export const filterByFlowLimit = (trips, minFlow = 0) => {
  const limit = Number(minFlow);
  if (limit <= 0) return trips;
  return trips.filter((trip) => Number(trip.trips) >= limit);
};

/**
 * Filter trips by origin regions
 */
export const filterByOrigins = (trips, origins = []) => {
  if (!origins || origins.length === 0) return trips;
  return trips.filter((trip) => origins.includes(trip.origin));
};

/**
 * Filter trips by destination regions
 */
export const filterByDestinations = (trips, destinations = []) => {
  if (!destinations || destinations.length === 0) return trips;
  return trips.filter((trip) => destinations.includes(trip.destination));
};



/**
 * Transform trip data to ECharts Sankey format
 * @param {Array} trips - Array of trip objects { origin, destination, trips }
 * @param {Object} options - Filter options
 * @returns {Object} ECharts Sankey data { nodes, links }
 */
export const transformToSankeyData = (trips, options = {}) => {
  const {
    minFlow = 0,
    origins = [],
    destinations = [],
    maxNodes = 50,
  } = options;

  // Apply filters
  let filteredTrips = [...trips];
  filteredTrips = filterByFlowLimit(filteredTrips, minFlow);
  filteredTrips = filterByOrigins(filteredTrips, origins);
  filteredTrips = filterByDestinations(filteredTrips, destinations);

  // Remove self-loops (origin === destination)
  filteredTrips = filteredTrips.filter((trip) => trip.origin !== trip.destination);

  // Sort by trips descending and limit
  filteredTrips.sort((a, b) => b.trips - a.trips);

  // Collect unique nodes
  const nodeSet = new Set();
  const links = [];

  filteredTrips.forEach((trip) => {
    // To distinguish origin and destination nodes in Sankey, we prefix them
    const sourceNode = `${trip.origin}`;
    const targetNode = `${trip.destination} `; // Add space suffix for destination

    nodeSet.add(sourceNode);
    nodeSet.add(targetNode);

    links.push({
      source: sourceNode,
      target: targetNode,
      value: Number(trip.trips),
    });
  });

  // Limit nodes if too many
  let nodes = Array.from(nodeSet).map((name) => ({
    name,
    // Clean display name (remove suffix space)
    displayName: name.trim(),
  }));

  if (nodes.length > maxNodes) {
    // Keep only the top flows
    const topLinks = links.slice(0, Math.floor(maxNodes / 2));
    const topNodeSet = new Set();
    topLinks.forEach((link) => {
      topNodeSet.add(link.source);
      topNodeSet.add(link.target);
    });
    nodes = Array.from(topNodeSet).map((name) => ({
      name,
      displayName: name.trim(),
    }));
    links.length = 0;
    links.push(...topLinks);
  }

  return {
    nodes,
    links,
    totalTrips: filteredTrips.reduce((sum, t) => sum + t.trips, 0),
    filteredCount: filteredTrips.length,
  };
};

/**
 * Get summary statistics for the Sankey data
 */
export const getSankeyStats = (sankeyData) => {
  const { nodes, links, totalTrips } = sankeyData;

  // Count origins and destinations
  const origins = nodes.filter((n) => !n.name.endsWith(' ')).length;
  const destinations = nodes.filter((n) => n.name.endsWith(' ')).length;

  // Find top flow
  const topFlow = links.length > 0 ? links[0] : null;

  return {
    nodeCount: nodes.length,
    linkCount: links.length,
    originCount: origins,
    destinationCount: destinations,
    totalTrips,
    topFlow: topFlow
      ? {
          from: topFlow.source.trim(),
          to: topFlow.target.trim(),
          value: topFlow.value,
        }
      : null,
  };
};
